import { ArrowUpRight, Clock } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { blogPosts } from "./blogData";
import TopbarInner from "../components/topbar/TopbarInner";
import { Helmet } from "react-helmet-async";
import { goBack } from "../utils/goBack";

export default function Blog({ onThemeToggle, showThemeDropdown = true }) {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Blog — Respiration et sons | Harmonauria</title>
        <meta name="description" content="Des repères simples pour comprendre la respiration guidée, la musique thérapeutique et leurs effets sur le stress, le sommeil et la concentration." />
        <link rel="canonical" href="https://harmonauria.fr/blog" />
      </Helmet>
<TopbarInner
  title="Blog"
  onBack={() => goBack(navigate)}
  onThemeToggle={onThemeToggle}
  titleColor="#36495B"
  showThemeDropdown={showThemeDropdown}
/>

      <main className="blog-page">
        <header className="page-hero">
          <p className="page-hero__eyebrow">
            Le journal Harmonauria
          </p>

          <h1 className="page-hero__title">Blog</h1>

          <p className="page-hero__lead">
            Des repères simples pour mieux comprendre la respiration,
            le son et leurs effets.
          </p>
        </header>

        <section
          className="blog-section"
          aria-label="Articles du journal Harmonauria"
        >
          <div className="blog-grid">
            {[...blogPosts].reverse().map((article) => (
              <article key={article.slug} className="blog-card">
<Link
  to={`/blog/${article.slug}`}
  state={{ from: "/blog" }}
  className="blog-card__link"
  aria-label={`Lire l’article : ${article.title}`}
>
                  <div className="blog-card__media">
                    <img
                      src={article.image}
                      alt=""
                      className="blog-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  <div className="blog-card__content">
                    <h2 className="blog-card-title">
                      {article.title}
                    </h2>

                    <p className="blog-excerpt">
                      {article.excerpt}
                    </p>

<div className="blog-card__footer">
  <span className="blog-meta">
    <Clock size={15} aria-hidden />
    <span>
      {article.readingTime} de lecture
    </span>
  </span>

  <span className="blog-card__cta" aria-hidden>
    Lire l’article
    <ArrowUpRight size={16} />
  </span>
</div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </section>
      </main>
      <style>{`
.blog-page,
.blog-page * {
  box-sizing: border-box;
}

.blog-page {
  width: 100%;
  min-height: 100%;
  margin-top: 88px;
  padding: 0 24px 80px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 42px;
}

/* Section */

.blog-page > .blog-section {
  width: min(1400px, calc(100vw - 48px));
  max-width: none !important;
  margin: 0 auto;

  align-self: center;

  padding: 0 !important;
  border: 0 !important;
}

.blog-grid {
  width: 100%;
  max-width: none !important;

  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;

  align-items: stretch;
  justify-items: stretch;
}

/* Carte */

.blog-card {
  position: relative;

  width: 100%;
  max-width: none !important;
  min-width: 0;

  display: flex;

  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(97, 131, 122, 0.16);
  border-radius: 24px;

  box-shadow:
    0 16px 38px rgba(54, 73, 91, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);

  overflow: hidden;

  transition:
    transform 260ms ease,
    border-color 260ms ease,
    box-shadow 260ms ease;
}

.blog-card__link {
  flex: 1;
  padding: 18px;

  display: flex;
  flex-direction: column;

  color: inherit;
  text-decoration: none;
  border-radius: inherit;
}

.blog-card__link:focus-visible {
  outline: 3px solid rgba(118, 165, 167, 0.28);
  outline-offset: -3px;
}

/* Image */

.blog-card__media {
  width: 100%;
  height: 320px;
  margin-bottom: 20px;

  border-radius: 18px;
  background: #edf3f1;

  overflow: hidden;
}

.blog-cover {
  width: 100%;
  height: 100%;

  display: block;
  object-fit: cover;

  transition: transform 500ms ease;
}

/* Contenu */

.blog-card__content {
  min-height: 0;
  flex: 1;

  display: flex;
  flex-direction: column;
}

.blog-card-title {
  margin: 0;

  color: #36495b;
  font-family: "Sora", system-ui, sans-serif;
  font-size: 1.16rem;
  font-weight: 700;
  line-height: 1.4;
}

.blog-excerpt {
  margin: 10px 0 0;
  flex: 1;

  color: #66757d;
  font-size: 0.94rem;
  line-height: 1.65;
}

/* Pied de carte */

.blog-card__footer {
  margin-top: 22px;
  padding-top: 15px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  border-top: 1px solid rgba(97, 131, 122, 0.11);
}

.blog-meta {
  display: inline-flex;
  align-items: center;
  gap: 7px;

  color: #7a898f;
  font-size: 0.8rem;
  font-weight: 500;
}

.blog-meta svg {
  flex: 0 0 auto;
  color: #718d85;
}

.blog-card__cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;

  color: #56766e;
  font-size: 0.84rem;
  font-weight: 650;

  transition:
    color 200ms ease,
    transform 200ms ease;
}

/* Hover uniquement sur ordinateur */

@media (hover: hover) and (pointer: fine) {
  .blog-card:hover {
    transform: translateY(-3px);
    border-color: rgba(97, 131, 122, 0.29);

    box-shadow:
      0 21px 46px rgba(54, 73, 91, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.85);
  }

  .blog-card:hover .blog-cover {
    transform: scale(1.025);
  }

  .blog-card:hover .blog-card__cta {
    color: #3f625a;
    transform: translateX(2px);
  }
}

/* Tablette et mobile */

@media (max-width: 767px) {
  .blog-page {
    margin-top: 94px;
    padding: 0 12px 64px;
    gap: 32px;
  }

  .blog-page > .blog-section {
    width: 100%;
    max-width: none !important;
  }

  .blog-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .blog-card {
    width: 100%;
    max-width: none !important;
    border-radius: 21px;
  }

  .blog-card__link {
    padding: 15px;
  }

  .blog-card__media {
    height: 190px;
    margin-bottom: 17px;
    border-radius: 16px;
  }

  .blog-card__footer {
    margin-top: 19px;
  }
}


/* Petits écrans */

@media (max-width: 390px) {
  .blog-card__footer {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
}

/* Aucun effet tactile persistant */

@media (hover: none), (pointer: coarse) {
  .blog-card,
  .blog-card:hover,
  .blog-cover,
  .blog-card:hover .blog-cover,
  .blog-card__cta,
  .blog-card:hover .blog-card__cta {
    transform: none;
  }
}

/* Réduction des animations */

@media (prefers-reduced-motion: reduce) {
  .blog-card,
  .blog-cover,
  .blog-card__cta {
    transition: none;
  }
}
`}</style>
    </>
  );
}
